import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Badge,
  Card,
  Code,
  Group,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import type {
  AnalyticsAttributionTier,
  AnalyticsClickStatus,
  AnalyticsShortlinkDisposition,
  AnalyticsShortlinkVisit,
  PaginatedResponse,
} from "../../../api.ts";
import { ADMIN_LIST_PAGE_SIZE, adminErrorMessage, adminGet } from "../../../api.ts";
import { CursorPagination } from "../../../components/CursorPagination.tsx";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { StatsTrendControls, type TrendRange } from "../../../components/StatsTrendControls.tsx";
import { todayInTz } from "@mip-tg-bot-turntable/utils";
import { BusinessDateTime } from "../../../components/BusinessDateTime.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { ANALYTICS_PLATFORM_FILTER_SEGMENTS } from "./platform-segments.ts";

const DISPOSITION_LABELS: Record<AnalyticsShortlinkDisposition, string> = {
  attributed: "正常归因",
  cookie_reuse: "Cookie复用",
  non_human: "非真人",
  rate_limited: "限流跳过",
  shared_fallback: "共享兜底",
  per_ad_primary: "Per-ad主链",
  per_ad_fallback: "Per-ad降级",
  natural_fallback: "自然兜底",
  macro_invalid: "宏未替换",
};

const DISPOSITION_COLOR: Record<AnalyticsShortlinkDisposition, string> = {
  attributed: "green",
  cookie_reuse: "blue",
  non_human: "gray",
  rate_limited: "orange",
  shared_fallback: "yellow",
  per_ad_primary: "cyan",
  per_ad_fallback: "teal",
  natural_fallback: "gray",
  macro_invalid: "red",
};

const STATUS_LABELS: Record<AnalyticsClickStatus, string> = {
  pending: "待入群",
  joined: "已入群",
  abandoned: "已放弃",
};

const STATUS_COLOR: Record<AnalyticsClickStatus, string> = {
  pending: "orange",
  joined: "green",
  abandoned: "gray",
};

const TIER_LABELS: Record<AnalyticsAttributionTier, string> = {
  per_click: "Per-click",
  per_ad: "Per-ad",
  natural: "自然",
};

const TIER_COLOR: Record<AnalyticsAttributionTier, string> = {
  per_click: "blue",
  per_ad: "teal",
  natural: "gray",
};

const PAGE_SIZE = ADMIN_LIST_PAGE_SIZE;

function TruncatedCode(props: { readonly value: string | null; readonly maxWidth?: number }) {
  const { value, maxWidth = 180 } = props;
  if (value === null || value === "") {
    return (
      <Text size="xs" c="dimmed">
        -
      </Text>
    );
  }
  return (
    <Tooltip label={value} multiline maw={400} withArrow>
      <Code
        style={{
          display: "block",
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "default",
        }}
      >
        {value}
      </Code>
    </Tooltip>
  );
}

const VALID_DISPOSITIONS = new Set<AnalyticsShortlinkDisposition>([
  "attributed",
  "cookie_reuse",
  "non_human",
  "rate_limited",
  "shared_fallback",
  "per_ad_primary",
  "per_ad_fallback",
  "natural_fallback",
  "macro_invalid",
]);

const parseDispositionParam = (raw: string | null): AnalyticsShortlinkDisposition | "all" => {
  if (raw === null || raw === "") return "all";
  return VALID_DISPOSITIONS.has(raw as AnalyticsShortlinkDisposition)
    ? (raw as AnalyticsShortlinkDisposition)
    : "all";
};

export function ClicksTab(props: { readonly botId: string; readonly timezone: string }) {
  const { botId, timezone } = props;
  const [searchParams] = useSearchParams();
  const [platformFilter, setPlatformFilter] = useState<
    "facebook" | "tiktok" | "google" | "sharechat" | "all"
  >("all");
  const [dispositionFilter, setDispositionFilter] = useState<AnalyticsShortlinkDisposition | "all">(
    () => parseDispositionParam(searchParams.get("visitDisposition")),
  );

  useEffect(() => {
    const fromUrl = parseDispositionParam(searchParams.get("visitDisposition"));
    setDispositionFilter(fromUrl);
  }, [searchParams]);
  const [adIdInput, setAdIdInput] = useState("");
  const [debouncedAdId] = useDebouncedValue(adIdInput, 300);
  const [range, setRange] = useState<TrendRange>("today");
  // keyset：cursors[i] 为本页请求用的 cursor（首页 null）
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = cursors[pageIndex] ?? null;

  const resetPages = () => {
    setCursors([null]);
    setPageIndex(0);
  };

  const list = useQuery({
    queryKey: [
      "bot",
      botId,
      "analytics-visits",
      range,
      platformFilter,
      dispositionFilter,
      debouncedAdId,
      cursor,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("pageSize", String(PAGE_SIZE));
      params.set("range", range);
      if (cursor !== null) params.set("cursor", cursor);
      if (platformFilter !== "all") params.set("platform", platformFilter);
      if (dispositionFilter !== "all") params.set("disposition", dispositionFilter);
      if (debouncedAdId.trim() !== "") params.set("adId", debouncedAdId.trim());
      return adminGet<PaginatedResponse<AnalyticsShortlinkVisit>>(
        `/analytics/${botId}/visits?${params.toString()}`,
      );
    },
  });

  const rows = (list.data?.items ?? []).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td style={{ whiteSpace: "nowrap" }}>
        <BusinessDateTime iso={item.createdAt} businessTimezone={timezone} />
      </Table.Td>
      <Table.Td>
        <Badge color={DISPOSITION_COLOR[item.disposition]} size="sm" variant="light">
          {DISPOSITION_LABELS[item.disposition]}
        </Badge>
      </Table.Td>
      <Table.Td>
        {item.platform === "facebook" ? (
          <Badge color="blue" size="sm">
            facebook
          </Badge>
        ) : item.platform === "tiktok" ? (
          <Badge color="violet" size="sm">
            tiktok
          </Badge>
        ) : item.platform === "google" ? (
          <Badge color="yellow" size="sm">
            google
          </Badge>
        ) : item.platform === "sharechat" ? (
          <Badge color="orange" size="sm">
            sharechat
          </Badge>
        ) : (
          <Badge color="gray" size="sm" variant="light">
            自然
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        {item.adId !== "" ? (
          <Text size="xs" ff="monospace">
            {item.adId}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.ip !== null ? (
          <Text size="xs" ff="monospace">
            {item.ip}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.country !== null ? (
          <Text size="xs">{item.country}</Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <TruncatedCode value={item.ua} maxWidth={160} />
      </Table.Td>
      <Table.Td>
        <TruncatedCode value={item.landingUrl} maxWidth={160} />
      </Table.Td>
      <Table.Td>
        {item.attributionTier !== null ? (
          <Badge color={TIER_COLOR[item.attributionTier]} size="sm" variant="light">
            {TIER_LABELS[item.attributionTier]}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.clickStatus !== null ? (
          <Badge color={STATUS_COLOR[item.clickStatus]} size="sm">
            {STATUS_LABELS[item.clickStatus]}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {item.tgUserId !== null ? (
          <Text size="xs" ff="monospace">
            {item.tgUserId}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td style={{ whiteSpace: "nowrap" }}>
        <BusinessDateTime iso={item.joinedAt} businessTimezone={timezone} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text fw={600} size="lg">
            访问记录
          </Text>
          <BotTimezoneNotice timezone={timezone} statsDay={todayInTz(timezone)} range={range} />
        </Group>
        <Text size="xs" c="dimmed">
          列表时间为业务时区。短链点击与处置；可按平台、广告 ID、处置筛选。
        </Text>
        <SegmentedControl
          fullWidth
          value={platformFilter}
          onChange={(v) => {
            setPlatformFilter(v as "facebook" | "tiktok" | "google" | "sharechat" | "all");
            resetPages();
          }}
          data={[...ANALYTICS_PLATFORM_FILTER_SEGMENTS]}
        />
      </Stack>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap">
            <StatsTrendControls
              range={range}
              granularity="hour"
              onRangeChange={(r) => {
                setRange(r);
                resetPages();
              }}
              onGranularityChange={() => {}}
              hideGranularity
            />
            <TextInput
              size="xs"
              placeholder="广告 ID"
              value={adIdInput}
              onChange={(e) => {
                setAdIdInput(e.currentTarget.value);
                resetPages();
              }}
              style={{ width: 200 }}
            />
            <Select
              size="xs"
              placeholder="处置方式"
              data={[
                { label: "全部", value: "all" },
                { label: DISPOSITION_LABELS.attributed, value: "attributed" },
                { label: DISPOSITION_LABELS.cookie_reuse, value: "cookie_reuse" },
                { label: DISPOSITION_LABELS.non_human, value: "non_human" },
                { label: DISPOSITION_LABELS.rate_limited, value: "rate_limited" },
                { label: DISPOSITION_LABELS.shared_fallback, value: "shared_fallback" },
                { label: DISPOSITION_LABELS.per_ad_primary, value: "per_ad_primary" },
                { label: DISPOSITION_LABELS.per_ad_fallback, value: "per_ad_fallback" },
                { label: DISPOSITION_LABELS.natural_fallback, value: "natural_fallback" },
                { label: DISPOSITION_LABELS.macro_invalid, value: "macro_invalid" },
              ]}
              value={dispositionFilter}
              onChange={(v) => {
                setDispositionFilter((v ?? "all") as AnalyticsShortlinkDisposition | "all");
                resetPages();
              }}
              style={{ width: 140 }}
              clearable={false}
            />
          </Group>
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : list.isError ? (
          <Text c="red" size="sm" ta="center" py="lg">
            {adminErrorMessage(list.error)}
          </Text>
        ) : (
          <>
            <AdminTable minWidth={1320}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>时间</Table.Th>
                  <Table.Th>处置方式</Table.Th>
                  <Table.Th>平台</Table.Th>
                  <Table.Th>广告 ID</Table.Th>
                  <Table.Th>IP</Table.Th>
                  <Table.Th>地区</Table.Th>
                  <Table.Th>UA</Table.Th>
                  <Table.Th>访问 URL</Table.Th>
                  <Table.Th>归因档位</Table.Th>
                  <Table.Th>入群状态</Table.Th>
                  <Table.Th>TG 用户</Table.Th>
                  <Table.Th>加群时间</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={12}>
                      <Text c="dimmed" size="sm" ta="center">
                        暂无记录
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </AdminTable>
            <CursorPagination
              pageSize={list.data?.pageSize ?? PAGE_SIZE}
              itemCount={list.data?.items.length ?? 0}
              hasMore={list.data?.hasMore === true}
              canGoPrev={pageIndex > 0}
              loading={list.isFetching}
              onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
              onNext={() => {
                const next = list.data?.nextCursor ?? null;
                if (next === null) return;
                setCursors((prev) => {
                  const head = prev.slice(0, pageIndex + 1);
                  return [...head, next];
                });
                setPageIndex((i) => i + 1);
              }}
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
